import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  buildWizardStepDependencyMeta,
  DEFAULT_TOASTER_LIFE,
  getWizardStepData,
  IWizardContext,
  persistWizardStepData,
  SelectOption,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import {
  SelectValue,
  STAND_CYCLE_ROTATION_COLUMN_HEADER_KEYS,
  toSingleSelectValue,
} from '../contract-assignment-by-stand.shared';
import { IStandCycleRotationRow } from '../interfaces/IStandCycleRotationRow';
import { ContractAssociation } from '../services/contract-association';
import { tap } from 'rxjs';
import { ISetPropertyTypeStepData } from '../interfaces/ISetPropertyTypeStepData';
import { IAssociateStandCycleRotationStepData } from '../interfaces/IAssociateStandCycleRotation';

@Component({
  selector: 'lib-step-set-property-type',
  standalone: true,
  imports: [LocalizationPipe, ZnSingleSelect],
  templateUrl: './step-set-property-type.html',
})
export class StepSetPropertyType implements WizardStepComponent<ISetPropertyTypeStepData> {
  private readonly toaster = inject(ToasterService);
  private readonly contractAssociationService = inject(ContractAssociation);
  readonly propertyTypeSelect = viewChild('propertyTypeSelect', { read: ZnSingleSelect });

  readonly localizationKeyPrefix = 'Geographic::ContractAssignmentByStand.StepSetPropertyType';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 3;

  readonly columnHeaderKeys = STAND_CYCLE_ROTATION_COLUMN_HEADER_KEYS;
  readonly propertyTypeOptions = signal<SelectOption<string>[]>([]);

  readonly selectedPropertyType = signal<string | null>(null);
  readonly rows = signal<IStandCycleRotationRow[]>([]);

  private readonly propertyTypes = new Map<string, string>();

  loadPropertyTypes = (params?: ZnSelectQueryParams) =>
    this.contractAssociationService.loadPropertyTypes(params).pipe(
      tap(response => {
        response.items.forEach(item => {
          this.propertyTypes.set(item.key, item.value);
        });
      }),
    );

  readonly propertyTypeSectionClass = computed(() =>
    this.selectedPropertyType()
      ? 'zn-step-content-section'
      : 'zn-step-content-section zn-step-content-section--selected',
  );

  readonly tableSectionClass = computed(() => {
    if (!this.selectedPropertyType()) {
      return 'zn-full-height zn-step-content-section';
    }

    return 'zn-full-height zn-step-content-section zn-step-content-section--selected';
  });
  properties: any;

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const previousStepState: IAssociateStandCycleRotationStepData | undefined = getWizardStepData(context, this.previousStepId);
    this.rows.set(previousStepState?.standCycleRotationRows ?? []);

    const previousData: ISetPropertyTypeStepData | undefined = getWizardStepData(context, context.activeStepId());
    
    if(previousData) this.loadPreviousData(previousData, context);
  }

  canLeaveSync(): boolean {
    return true;
  }

  onPropertyTypeChange(value: SelectValue): void {
    const propertyTypeId = toSingleSelectValue(value);
    this.selectedPropertyType.set(propertyTypeId);
    this.applyPropertyTypeToRows(this.resolvePropertyTypeName(propertyTypeId));
  }

  canLeave(): boolean {
    if (this.selectedPropertyType()) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::ContractAssignmentByStand.StepSetPropertyType.cantContinue`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  collect(context: IWizardContext): ISetPropertyTypeStepData {
    const propertyId = this.selectedPropertyType() as string;
    const propertyName = this.resolvePropertyTypeName(propertyId);
    const standCycleRotationRows = this.rows().map(row => ({
      ...row,
      propertyType: propertyName,
    }));

    this.rows.set(standCycleRotationRows);

    const data: ISetPropertyTypeStepData = {
      propertyTypeId: this.selectedPropertyType(),
      propertyTypeName: propertyName as string,
      standCycleRotationRows,
    };
    persistWizardStepData(context, {
      valid: !!this.selectedPropertyType(),
      visited: true,
      completed: true,
      data: data,
      dirty: true,
      meta: buildWizardStepDependencyMeta(data)
    });
    return data;
  }

  private loadPreviousData(data: ISetPropertyTypeStepData, context: IWizardContext): void {
    this.selectedPropertyType.set(data.propertyTypeId);
    if (data.propertyTypeId && data.propertyTypeName) {
      this.propertyTypes.set(data.propertyTypeId, data.propertyTypeName);
    }
    this.applyPropertyTypeToRows(data.propertyTypeName);

    this.propertyTypeOptions.set(
      data.propertyTypeId && data.propertyTypeName
        ? [{ key: data.propertyTypeId, value: data.propertyTypeName }]
        : [],
    );

    queueMicrotask(() => {
      this.propertyTypeSelect()?.writeValue(data.propertyTypeId);
    });

    context.patchStepState(context.activeStepId(), {
      visited: true,
      dirty: true,
      completed: true,
      data: data
    });
  }

  private resolvePropertyTypeName(propertyTypeId: string | null): string {
    if (!propertyTypeId) {
      return '';
    }

    return (
      this.propertyTypes.get(propertyTypeId) ??
      this.propertyTypeOptions().find(option => option.key === propertyTypeId)?.value ??
      ''
    );
  }

  private applyPropertyTypeToRows(propertyTypeName: string): void {
    this.rows.update(rows =>
      rows.map(row => ({
        ...row,
        propertyType: propertyTypeName,
      })),
    );
  }
}
